import { useCallback, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FormikErrors, useFormik } from "formik";
import moment from "moment";
import * as yup from "yup";

import pathnames from "routes/pathnames";

import api from "services/api";
import getArticleCategory from "services/get-article-category";

import formatDateTime from "common/format-date-time";
import serveRequestErrors from "common/serve-request-errors";
import serveRequestSuccess from "common/serve-request-success";

import ERRORS from "constants/errors";
import CONSTANTS from "constants/constant";
import bodyInputType from "constants/body-input-type";
import { statusOptions } from "constants/status-options";

import ActionEnum from "enum/action";
import BodyTypeEnum from "enum/body-type";

import ErrorResponseDto from "dto/services/error-response-dto";
import PageArticleDto, { BodyDto, MediaDto } from "dto/pages/articles/page-article-dto";

import AppInput from "components/app-input";
import AppEditor from "components/app-editor";
import AppButton from "components/app-button";
import AppHeading from "components/app-heading";
import AppLinkInput from "components/app-link-input";
import AppBreadcrumbs from "components/app-breadcrumbs";
import AppSelectInput from "components/app-select-input";

import closeIcon from "assets/images/pages/articles/close-icon.svg";
import addIcon from "assets/images/components/app-link-input/add-icon.svg";

const PageArticle = () => {
	const navigate = useNavigate();
	const { id } = useParams();
	const { date, time } = formatDateTime(String(new Date()));

	const isAddNew = useMemo(() => (id === ActionEnum.Create ? true : false), [id]);
	const buttonLabel = useMemo(() => (isAddNew ? "Add" : "Save"), [isAddNew]);

	//prettier-ignore
	const breadcrumbItems = useMemo(() => [
        { label: "Articles Learning Centre", href: pathnames.articles },
        { label: isAddNew ? "Add New" : "Edit Article", href: isAddNew ? pathnames.articles + "/create" : `${pathnames.articles}/${id}` },
    ], [id, isAddNew]);

	//prettier-ignore
	const validationSchema = yup.object({
		title: yup.string().max(90).required(ERRORS.REQUIRED),
		thumbnail: yup.string().required(ERRORS.REQUIRED),
		categoryId: yup.string().required(ERRORS.REQUIRED),
		status: yup.string().required(ERRORS.REQUIRED),
		location: yup.string().required(ERRORS.REQUIRED),
		tableOfContent: yup.array().min(1, ERRORS.REQUIRED),
		content: yup.array().of(
            yup.object({
                contentType: yup.string().required(ERRORS.REQUIRED),
                subTitle: yup.string().when("contentType", {
                    is: (type: string) => type === BodyTypeEnum.Text,
                    then: () => yup.string().required(ERRORS.REQUIRED),
                }),
                subContent: yup.string().when("contentType", {
                    is: (type: string) => type === BodyTypeEnum.Text,
                    then: () => yup.string().required(ERRORS.REQUIRED),
                }),
                medias: yup.array().when("contentType", {
                    is: (type: string) => type === BodyTypeEnum.Image,
                    then: () => yup.array().of(
						yup.object({
							mediaLink: yup.string().required(ERRORS.REQUIRED),
						  })
					).min(1, ERRORS.AT_LEAST_ONE).required(ERRORS.REQUIRED)
                }),
            })
        ).min(1, ERRORS.REQUIRED),
	});

	const formik = useFormik<PageArticleDto>({
		initialValues: { title: "", thumbnail: "", categoryId: "", status: "", location: "", publicationDate: date, updatedAt: `${date} | ${time}`, tableOfContent: [], attachments: [], content: [] },
		validationSchema,
		onSubmit: (values) => {
			onHandleSubmit(values);
		},
	});

	const isBodyInvalid = useMemo(() => Boolean(formik.errors.content) && Boolean(formik.touched.content) && formik.values.content.length === 0, [formik.errors.content, formik.touched.content, formik.values.content.length]);

	//prettier-ignore
	const onHandleSubmit = useCallback(async (values: PageArticleDto) => {
		let response = null;

		try {
			const payload = {
				id: values.id || "",
				title: values.title,
				thumbnail: values.thumbnail,
				categoryId: values.categoryId,
				status: values.status,
				displayStatus: values.status,
				location: values.location,
				publicationDate: moment(values?.publicationDate, "DD/MM/YYYY").toISOString() || "",
				tableOfContent: values.tableOfContent,
				attachments: values.attachments,
				content: values.content.map((item) => ({ ...item, medias: item.medias?.map((media) => ({
					mediaType: BodyTypeEnum.Image, 
					mediaLink: media.mediaLink,
				})) || [] })),
			};

			if (isAddNew) {
				response = await api.post.articles.create(payload);
			} else if (values.id) {
				response = await api.put.articles.update(payload);
			}
		} catch (error) {
			const err = error as Error | ErrorResponseDto;

			serveRequestErrors(err);
		}

		if (response) {
			navigate(pathnames.articles);

			if (isAddNew) serveRequestSuccess(CONSTANTS.MESSAGES.CREATED, ActionEnum.Create);
			else serveRequestSuccess(CONSTANTS.MESSAGES.UPDATED, ActionEnum.Update);
		}
	}, [isAddNew, navigate]);

	const onHandleAddNewSection = () => {
		formik.setFieldValue("content", [...formik.values.content, { contentType: "", subTitle: "", subContent: "", medias: [] }]);
	};

	const onHandleRemoveSection = (index: number) => {
		const newValue = formik.values.content.filter((_, i) => i !== index);

		formik.setFieldValue("content", newValue);
	};

	const onHandleGetArticleDetails = useCallback(async () => {
		try {
			const response = await api.get.articles.article({ id: id! });

			const articleDetails = response.data.data;

			//prettier-ignore
			const contentWithDefaults = articleDetails.content?.map((o: any) => ({
				...o,
				contentType: o.contentType || BodyTypeEnum.Text,
				medias: o.medias?.map((media: any) => ({
					mediaType: media.mediaType,
					mediaLink: media.mediaLink,
				})) || [],
			})) || [];

			formik.setValues({
				id: articleDetails.id || "",
				title: articleDetails.title || "",
				thumbnail: articleDetails.thumbnail || "",
				categoryId: articleDetails.categoryId || "",
				status: articleDetails.status || "",
				location: articleDetails.location || "",
				publicationDate: articleDetails.publicationDate ? moment(articleDetails.publicationDate).format(CONSTANTS.LISTING_DATE_FORMAT) : "", //articleDetails.date ? moment(articleDetails.date).format(CONSTANTS.LISTING_DATE_FORMAT) : "",
				updatedAt: articleDetails.updatedAt ? moment(articleDetails.updatedAt).format(CONSTANTS.LISTING_DATE_FORMAT) : "", //formatLastUpdate(articleDetails.updatedDate),
				tableOfContent: articleDetails.tableOfContent || [],
				attachments: articleDetails.attachments || [],
				content: contentWithDefaults,
			});
		} catch (error) {
			const err = error as Error | ErrorResponseDto;

			serveRequestErrors(err);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id, formik.setValues]);

	const onHandleDeleteArticle = useCallback(async () => {
		try {
			const response = await api.put.articles.delete({ id: id! });

			if (response.status === 200) {
				navigate(pathnames.articles);

				serveRequestSuccess(CONSTANTS.MESSAGES.UPDATED, ActionEnum.Update);
			}
		} catch (error) {
			const err = error as Error | ErrorResponseDto;

			serveRequestErrors(err);
		}
	}, [id, navigate]);

	const onHandleImageCountChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
		const newCount = parseInt(e.target.value, 10);
		const updatedContent: BodyDto[] = [...formik.values.content];

		const currentMedias: MediaDto[] = updatedContent[index].medias || [];
		if (newCount > currentMedias.length) {
			updatedContent[index].medias = [...currentMedias, ...Array(newCount - currentMedias.length).fill({ mediaType: BodyTypeEnum.Image, mediaLink: "" })];
		} else {
			updatedContent[index].medias = currentMedias.slice(0, newCount);
		}

		formik.setFieldValue("content", updatedContent);
	};

	useEffect(() => {
		if (!isAddNew) onHandleGetArticleDetails();
	}, [isAddNew, onHandleGetArticleDetails]);

	return (
		<div className="page-article">
			<div className="article">
				<AppHeading title="Articles Learning Centre">
					<AppHeading.Divider />
				</AppHeading>

				<form className="article__container" onSubmit={formik.handleSubmit}>
					<div className="article__header">
						<AppBreadcrumbs items={breadcrumbItems} />

						<div className="article__button-wrapper">
							{!isAddNew && <AppButton type="button" label="Delete" disabled={formik.isSubmitting} text onClick={onHandleDeleteArticle} />}

							<AppButton type="submit" label={buttonLabel} disabled={formik.isSubmitting} />
						</div>
					</div>

					<div className="form">
						<p className="form__title">Article Setting</p>

						<div className="form__wrapper">
							{/* prettier-ignore */}
							<div className="full-width">
							    <AppInput type="text" name="title" label="Article Headline" placeholder="Enter article headline" value={formik.values.title} touched={formik.touched.title} error={formik.errors.title} onChange={formik.handleChange} maxLength={90} required />
                            </div>

							{/* prettier-ignore */}
							<AppInput type="text" name="thumbnail" label="Cover Photo" placeholder="Upload cover" value={formik.values.thumbnail} touched={formik.touched.thumbnail} error={formik.errors.thumbnail} onChange={formik.handleChange} required tooltipContent="Recommended dimensions: (1080x1920)px or 6:9 ratio" />

							{/* prettier-ignore */}
							<AppSelectInput type="text" name="categoryId" label="Category" placeholder={"Please select"} value={formik.values.categoryId} touched={formik.touched.categoryId} error={formik.errors.categoryId} loadOptions={getArticleCategory} onChange={formik.handleChange} required />

							{/* prettier-ignore */}
							<AppInput type="text" name="updatedAt" label="Last Update" value={formik.values.updatedAt} touched={formik.touched.updatedAt} error={formik.errors.updatedAt} onChange={formik.handleChange} disabled />

							{/* prettier-ignore */}
							<AppInput type="text" name="publicationDate" label="Publication Date" value={formik.values.publicationDate} touched={formik.touched.publicationDate} error={formik.errors.publicationDate} onChange={formik.handleChange} disabled />

							{/* prettier-ignore */}
							<AppSelectInput type="text" name="status" label="Status" placeholder="Please select" value={formik.values.status} touched={formik.touched.status} error={formik.errors.status} options={statusOptions} onChange={formik.handleChange} required />

							{/* prettier-ignore */}
							<AppInput type="text" name="location" label="Location" placeholder="Location" value={formik.values.location} touched={formik.touched.location} error={formik.errors.location} onChange={formik.handleChange} required />

							{/* prettier-ignore */}
							<AppLinkInput label="Tag / Table of Contents" touched={formik.touched.tableOfContent} errors={formik.errors.tableOfContent} onChange={(updatedTags) => formik.setFieldValue("tableOfContent", updatedTags)} inputValues={formik.values.tableOfContent} required />

							{/* prettier-ignore */}
							<AppLinkInput label="Document Type" inputValues={formik.values.attachments.map((attachment) => attachment.url || "")} touched={formik.touched.attachments && formik.touched.attachments.length > 0} errors={formik.errors.attachments} onChange={(updatedDocuments) => formik.setFieldValue("attachments", updatedDocuments.map((url) => ({ title: "", url })))} />
						</div>
					</div>

					<div className="form">
						{/* prettier-ignore */}
						<p className="form__title">Body<span className="form__required"> *</span></p>

						<div>
							{formik.values.content.map((o, i) => {
								const names = `content[${i}]`;
								const values = formik.values.content[i];
								const errors = formik.errors.content?.[i] as FormikErrors<BodyDto> | undefined;
								const touched = formik.touched.content?.[i];
								const typeValue = formik.values.content[i]?.contentType;

								const mediasTouched = touched?.medias as boolean[] | undefined;
								const mediasErrors = errors?.medias as { mediaLink?: string }[] | undefined;

								return (
									<div className="form__body-wrapper" key={i}>
										<div className="form__body-close" onClick={() => onHandleRemoveSection(i)}>
											<img src={closeIcon} alt="Close" />
										</div>

										<div className="form__body-item">
											{/* prettier-ignore */}
											<AppSelectInput type="text" name={`${names}.contentType`} label="Type" placeholder="Please select" value={values.contentType} touched={touched?.contentType} error={errors?.contentType} options={bodyInputType} onChange={formik.handleChange} required />

											{/* prettier-ignore */}
											{typeValue === BodyTypeEnum.Text && (
												<>
													{/* prettier-ignore */}
													<AppInput type="text" name={`${names}.subTitle`} label="Title" placeholder="Enter title" value={values.subTitle} touched={touched?.subTitle} error={errors?.subTitle} onChange={formik.handleChange} required />

													{/* prettier-ignore */}
													<AppEditor name={`${names}.subContent`} label="Description" placeholder="Enter description" value={values.subContent} touched={touched?.subContent} error={errors?.subContent} onChange={(content) => formik.setFieldValue(`${names}.subContent`, content)} required />
												</>
											)}

											{typeValue === BodyTypeEnum.Image && (
												<>
													{/* prettier-ignore */}
													<AppSelectInput name="imageCount" label="Number of Images" value={values.medias?.length} touched={touched?.medias} error={ touched?.medias &&  mediasErrors && values.medias?.length === 0 ? ERRORS.AT_LEAST_ONE : undefined } onChange={(e) => onHandleImageCountChange(e, i)} options={Array.from({ length: 8 }, (_, count) => ({ value: count + 1, label: `${count + 1}` }))} required />

													{values.medias?.map((media, imgIndex) => (
														<div key={imgIndex}>
															{/* prettier-ignore */}
															<AppInput type="text" name={`content[${i}].medias[${imgIndex}].mediaLink`} label={`Image ${imgIndex + 1} URL`} value={media.mediaLink} touched={mediasTouched?.[imgIndex]} error={mediasErrors?.[imgIndex]?.mediaLink} onChange={formik.handleChange} required />
														</div>
													))}
												</>
											)}
										</div>
									</div>
								);
							})}

							<div className={`form__add-section-wrapper ${isBodyInvalid ? "form__add-section-wrapper--error" : ""}`} onClick={onHandleAddNewSection}>
								<img src={addIcon} alt="Add" />

								<p className="form__add-section-text">Add Section</p>
							</div>

							{isBodyInvalid && <p className="form__text-error">{ERRORS.REQUIRED}</p>}
						</div>
					</div>
				</form>
			</div>
		</div>
	);
};

export default PageArticle;
